/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Button, Icon, Theme, useTheme } from 'bold-ui'
import { BreadcrumbNav } from 'components/breadcrumb'
import { BadgeNotificacao } from 'components/notificacao/BadgeNotificacao'
import { UserHeader } from 'components/user/UserHeader'
import { TipoTopicoNotificacaoEnum } from 'graphql/types.generated'
import { useNotificacaoCount } from 'hooks/notificacao/useNotificacaoCount'
import { useState } from 'react'

import { PageContainer } from './PageContainer'
import { SIDE_MENU_WIDTH } from './SideMenu'

export const Z_INDEX_MAIN_HEADER = 12

export interface MainHeaderProps {
  hasBreadcrumbs: boolean
  onModulosClick(e: React.MouseEvent): void
}

/**
 * Header da aplicação, contendo a logo do e-sus, menu do usuário e breadcrumbs.
 */
export const MainHeader = (props: MainHeaderProps) => {
  const { hasBreadcrumbs, onModulosClick } = props
  const theme = useTheme()
  const styles = createStyles(theme)
  const [isHovered, setIsHovered] = useState(false)

  const hasNotificacoes = useNotificacaoCount(Object.values(TipoTopicoNotificacaoEnum)) > 0

  return (
    <div css={styles.wrapper}>
      <UserHeader />

      {hasBreadcrumbs && (
        <div css={styles.container}>
          <PageContainer fluid>
            <div css={styles.breadcrumbs} data-cy='AppHeader.breadcrumbs'>
              <span css={styles.modulos}>
                <Button
                  size='small'
                  skin='ghost'
                  kind='primary'
                  onClick={onModulosClick}
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                >
                  <BadgeNotificacao
                    value={hasNotificacoes}
                    type='small'
                    style={{
                      position: styles.badgePosition,
                      border: styles.badgeBorder,
                    }}
                    isHovered={isHovered}
                  >
                    <Icon icon='module' style={{ marginRight: '0.5rem' }} />
                  </BadgeNotificacao>
                  Módulos
                </Button>
              </span>

              <BreadcrumbNav />
            </div>
          </PageContainer>
        </div>
      )}
    </div>
  )
}

const createStyles = (theme: Theme) => ({
  wrapper: css`
    position: relative;
    z-index: ${Z_INDEX_MAIN_HEADER};
    box-shadow: 0 1px 5px 0 rgba(0, 0, 0, 0.12), 0 2px 1px -1px rgba(0, 0, 0, 0.04);
  `,
  container: css`
    border-bottom: 1px solid ${theme.pallete.divider};
    background: ${theme.pallete.surface.main};
    ${theme.breakpoints.up('lg')} {
      padding-left: ${SIDE_MENU_WIDTH}px;
    }
  `,
  breadcrumbs: css`
    display: flex;
    align-items: center;
  `,
  modulos: css`
    margin-right: 0.5rem;
    padding-right: 0.5rem;
    border-right: 1px solid ${theme.pallete.divider};

    ${theme.breakpoints.up('lg')} {
      display: none;
    }
  `,
  badgePosition: css`
    top: -0.5px;
    right: 8px;
  `,
  badgeBorder: (isHovered: boolean) => css`
    border: 2px solid ${isHovered ? theme.pallete.gray.c90 : theme.pallete.surface.main};
    transition: border-color 0.2s;
  `,
})
